import { useState, useCallback, useLayoutEffect, useMemo } from 'react'
import WalkthroughDS from '@web-ui-kit/walkthrough'
import PropTypes from 'prop-types'
import * as Sc from './Walkthrough.style'
import { ALIGN, POSITION, TYPE } from './Walkthrough.helper'
import { useSetFullWalkthroughFlag } from 'hooks/user'
import debounce from 'utils/debounce'
import { STATUS_FLAGS } from 'utils/constants/flags'
import useMediaQuery from 'hooks/useMediaQuery'

const Walkthrough = ({ className, onClose, steps }) => {
  const [stepId, setStepId] = useState(null)
  const [coords, setCoords] = useState(null)
  const { mutate: mutateFullWalkthrough } = useSetFullWalkthroughFlag(
    STATUS_FLAGS.FALSE
  )
  const { isLaptop } = useMediaQuery()

  let contentElement = document.getElementById(`content-walkthrough-${stepId}`)
    ?.firstElementChild?.firstElementChild

  const selectedStep = useMemo(() =>
    steps.find((step) => step?.id === stepId, [stepId])
  )

  const updateCoords = useCallback(() => {
    if (stepId) {
      const focusElement = document.getElementById(stepId)
      contentElement = document.getElementById(`content-walkthrough-${stepId}`)
        ?.firstElementChild?.firstElementChild
      focusElement?.scrollIntoView({ block: 'end' })
      const focusElementCoords = focusElement?.getBoundingClientRect()
      const contentElementCoords = contentElement?.getBoundingClientRect()
      const currentCoords = JSON.parse(JSON.stringify(focusElementCoords || {}))
      currentCoords.widthContent = contentElementCoords?.width
      currentCoords.heightContent = contentElementCoords?.height
      currentCoords.windowWidth = window.innerWidth
      currentCoords.windowHeight = window.innerHeight
      setCoords(currentCoords)
    }
  }, [stepId, steps])

  const debouncedUpdateCoords = useCallback(
    debounce(() => updateCoords(), 100),
    [stepId]
  )

  useLayoutEffect(() => {
    if (contentElement) {
      updateCoords()
    }
  }, [contentElement])

  useLayoutEffect(() => {
    if (!stepId) {
      setCoords(null)
    }
    if (stepId) {
      updateCoords()
      window.addEventListener('resize', debouncedUpdateCoords)
      window.addEventListener('scroll', debouncedUpdateCoords)
    }
    return () => {
      window.removeEventListener('resize', debouncedUpdateCoords)
      window.removeEventListener('scroll', debouncedUpdateCoords)
    }
  }, [stepId, steps])

  const handleStepId = (id) => {
    setStepId(id)
  }

  const handleClose = () => {
    if (onClose) onClose()
    else mutateFullWalkthrough()
  }
  const { location } = selectedStep || {}
  const currentLocation = isLaptop ? location?.tablet : location?.web

  return (
    <Sc.Overlay $enable={!!stepId}>
      {stepId && (
        <Sc.Focus
          $coords={coords}
          data-testid={`focus-walkthrough-${stepId}`}
        />
      )}
      <Sc.Wrapper
        $coords={coords}
        $location={currentLocation}
        className={className}
        id={`content-walkthrough-${stepId}`}
      >
        <WalkthroughDS
          onClose={handleClose}
          onStep={handleStepId}
          steps={steps}
        />
      </Sc.Wrapper>
    </Sc.Overlay>
  )
}

Walkthrough.defaultProps = {
  className: null,
  onClose: null,
}
Walkthrough.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      buttons: PropTypes.shape({
        nextTitle: PropTypes.string.isRequired,
        previousTitle: PropTypes.string,
      }),
      id: PropTypes.string,
      image: PropTypes.shape({
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      location: PropTypes.shape({
        mobile: PropTypes.shape({
          align: PropTypes.oneOf([ALIGN.START, ALIGN.END, ALIGN.CENTER]),
          position: PropTypes.oneOf([
            POSITION.BOTTOM,
            POSITION.CENTER,
            POSITION.IN_LINE,
            POSITION.TOP,
          ]),
        }),
        tablet: PropTypes.shape({
          align: PropTypes.oneOf([ALIGN.START, ALIGN.END, ALIGN.CENTER]),
          position: PropTypes.oneOf([
            POSITION.BOTTOM,
            POSITION.CENTER,
            POSITION.IN_LINE,
            POSITION.TOP,
          ]),
        }),
        web: PropTypes.shape({
          align: PropTypes.oneOf([ALIGN.START, ALIGN.END, ALIGN.CENTER]),
          position: PropTypes.oneOf([
            POSITION.BOTTOM,
            POSITION.CENTER,
            POSITION.IN_LINE,
            POSITION.TOP,
          ]),
        }),
      }),
      lottie: PropTypes.shape({}),
      newFeatureText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      type: PropTypes.oneOf([TYPE.BIG, TYPE.SMALL]),
    })
  ).isRequired,
}

export default Walkthrough
